<template>
  <div class="buy-sell-control-stop-limit">
    <trade-buy-sell-header
      :trade-type="tradeType"
      :title="formattedTradeType"
      :main-ticker="getCurrentPair.stockTicker"
      :balance="formattedBalance"
      :theme="submitTheme"
      class="buy-sell-control-stop-limit__head"
    />

    <form
      autocomplete="off"
      class="pb-20 pt-16 pl-10 pr-10"
      @submit.prevent="handleSubmit"
      @keydown.enter.prevent
    >
      <trade-buy-sell-input
        v-model="stop"
        :field="$v.stop"
        :optional-errors="{
          greaterThanZero: $t('validators.greaterThanZero'),
          ...mix_getServerError('stop'),
          ...commonValidationTranslations,
        }"
        :placeholder="$t('trade.buySell.stop')"
        :currency="getCurrentPair.moneyTicker"
        :size="size"
        autocomplete="off"
        type="number"
        step="0.00000001"
        class="buy-sell-control-stop-limit__stop mb-16"
        @input="onInputStop"
        @blur="$v.stop.$touch()"
      />

      <trade-buy-sell-input
        v-model="price"
        :field="$v.price"
        :optional-errors="{
          greaterThanZero: $t('validators.greaterThanZero'),
          ...mix_getServerError('price'),
          ...commonValidationTranslations,
        }"
        :placeholder="$t('trade.buySell.limit')"
        :currency="getCurrentPair.moneyTicker"
        :size="size"
        autocomplete="off"
        type="number"
        step="0.00000001"
        class="buy-sell-control-stop-limit__limit mb-16"
        @input="onInputPrice"
        @blur="$v.price.$touch()"
      />
      <!-- <pre>{{ amountInData }}</pre> -->
      <!-- <pre>{{ amount }}</pre> -->
      <!-- <pre>{{ $v.amount.$model }}</pre> -->
      <trade-buy-sell-input
        v-model="amount"
        :field="$v.amount"
        :optional-errors="{
          ...mix_getServerError('amount'),
          ...commonValidationTranslations,
        }"
        :placeholder="$t('trade.buySell.amount')"
        :currency="getCurrentPair.stockTicker"
        :size="size"
        autocomplete="off"
        type="number"
        step="0.00000001"
        class="buy-sell-control-stop-limit__amount mb-16"
        @input="onInputAmount"
        @blur="$v.amount.$touch()"
      />
      <trade-buy-sell-amount-picker
        :buttons="percentBtns"
        class="buy-sell-control__percentage mb-8"
        @click="amountQuickPicker"
      />
      <trade-buy-sell-input
        v-model="total"
        :field="$v.total"
        :optional-errors="{
          ...mix_getServerError('total'),
          ...commonValidationTranslations,
        }"
        :placeholder="$t('trade.buySell.total')"
        :currency="getCurrentPair.moneyTicker"
        :size="size"
        :current-type="tradeType"
        autocomplete="off"
        type="number"
        step="0.00000001"
        class="buy-sell-control-stop-limit__total mb-20"
        @input="onInputTotal"
        @blur="$v.total.$touch()"
      />
      <exc-button
        v-if="logged"
        :color="submitTheme"
        :loading="loading"
        size="xs"
        class="buy-sell-control-stop-limit__btn"
        full-width
        type="submit"
      >
        {{ tradeType === TRADE_TYPE.BUY ? $t('trade.buy') : $t('trade.sell') }}
      </exc-button>
      <exc-button
        v-else
        color="main"
        outline
        :loading="loading"
        :disabled="$v.$error"
        size="xs"
        class="buy-sell-control-stop-limit__btn"
        :to="localePath('/login')"
        full-width
      >
        {{ $t('trade.loginBtn') }}
      </exc-button>
    </form>
  </div>
</template>

<script>
import { ConfirmationModal } from '@/lib/ConfirmationModal'
import {
  BUY_SELL_FORM_STOP_LIMIT,
  MARGIN_TRADE_CROSS,
  TRADE_TYPE,
} from '@/lib/constants'
import { valueSetterCutter, noExponents } from '@/lib/utils'
import { serverFormMixin } from '@/mixins/serverFormMixin'
import { Decimal } from 'decimal.js-light'
import * as lockr from 'lockr'
import { ExcButton } from '@/components/common'
import { validationMixin } from 'vuelidate'
import { maxValue, minValue, required } from 'vuelidate/lib/validators'
import { mapGetters, mapState } from 'vuex'

export default {
  name: 'BuySellControlStopLimit',
  components: {
    ExcButton,
    TradeBuySellHeader: () =>
      import(
        /* webpackMode: "eager" */ '@/components/trade/trade-buy-sell-elements/TradeBuySellHeader.vue'
      ),
    TradeBuySellInput: () =>
      import(
        /* webpackMode: "eager" */ '@/components/trade/trade-buy-sell-elements/TradeBuySellInput.vue'
      ),
    TradeBuySellAmountPicker: () =>
      import(
        /* webpackMode: "eager" */ '@/components/trade/trade-buy-sell-elements/TradeBuySellAmountPicker.vue'
      ),
    // MarginTradeModeSelector: () =>
    //   import(
    //     /* webpackMode: "eager" */ '@/components/trade/trade-buy-sell-elements/MarginTradeModeSelector.vue'
    //   ),
  },
  mixins: [validationMixin, serverFormMixin],
  props: {
    tradeType: {
      validator: v => v === TRADE_TYPE.BUY || v === TRADE_TYPE.SELL,
      required: true,
    },
    size: {
      type: String,
      default: null,
    },
  },
  data () {
    return {
      currentMarginType: 'borrow',
      percentBtns: [
        { name: '25%', type: 0.25 },
        { name: '50%', type: 0.5 },
        { name: '75%', type: 0.75 },
        { name: '100%', type: 1 },
      ],
      loading: false,
      stop: null,
      // limit: null, // price
      // amount: null,
      // total: null,

      TRADE_TYPE,
    }
  },
  computed: {
    ...mapState({
      logged: state => state.auth.loggedIn,
      clearErrorsTimeToWatch: state =>
        state.buySell.form.clearErrorsTimeToWatch,
    }),
    ...mapGetters([
      // "getCurrentPair",
      // "getCurrencyAvailableBalance",
    ]),
    ...mapGetters({
      getCurrentPair: 'pairs/getCurrentPair',
      // getMarginBalanceBuyPairAndTicker: 'wallet/getMarginBalanceBuyPairAndTicker',
      getCurrencyAvailableBalance: 'wallet/getCurrencyAvailableBalance',
      getMarginBalanceByTicker: 'wallet/getMarginBalanceByTicker',
    }),
    formattedTradeType () {
      return this.tradeType === TRADE_TYPE.BUY
        ? this.$t('trade.buy')
        : this.$t('trade.sell')
    },
    formattedBalance () {
      return ` ${
        this.balance.greaterThan(0)
          ? noExponents(this.balance.toDecimalPlaces(8).toNumber())
          : '0'
      } ${this.balanceTicker ? this.balanceTicker : ''}`
    },
    isMarginTrade () {
      return this.$store.state.trade.currentTradeType === MARGIN_TRADE_CROSS
    },
    balance () {
      // console.log(this.$store.state.currentTradeType)
      if (this.isMarginTrade) {
        return this.getMarginBalanceByTicker(this.balanceTicker).marginBalance
      }
      return this.getCurrencyAvailableBalance(this.balanceTicker)
    },
    balanceTicker () {
      return this.tradeType === TRADE_TYPE.BUY
        ? this.getCurrentPair.moneyTicker // second
        : this.getCurrentPair.stockTicker // first
    },
    price: {
      get () {
        return this.valueGetter('price')
      },
      set (val) {
        this.valueSetter(val, 'setBuySellFormPrice')
        this.$v.price.$touch()
      },
    },
    amount: {
      get () {
        return this.valueGetter('amount')
      },
      set (val) {
        this.valueSetter(val, 'setBuySellFormAmount')
        this.$v.amount.$touch()
      },
    },
    total: {
      get () {
        return this.valueGetter('total')
      },
      set (val) {
        this.valueSetter(val, 'setBuySellFormTotal')
        this.$v.total.$touch()
      },
    },
    commonValidationTranslations () {
      return {
        maxValue: this.$t('validators.maxValue', {
          max: this.balance.toNumber(),
        }),
        required: this.$t('validators.required'),
        minValue: this.$t('validators.minValue', {
          min: this.getCurrentPair.minAmountStock,
        }),
      }
    },
    submitTheme () {
      return this.tradeType === TRADE_TYPE.BUY ? 'green' : 'red'
    },
  },

  watch: {
    tradeType () {
      this.amount = null
      this.$v.$reset()
    },
    clearErrorsTimeToWatch () {
      this.$v.$reset()
    },
  },

  methods: {
    onInputStop (v) {
      this.stop = valueSetterCutter(v)
      this.mix_clearServerError('stop')
      this.$v.stop.$touch()
    },
    onInputPrice (v) {
      this.price = valueSetterCutter(v)
      this.mix_clearServerError('price')
      this.$v.price.$touch()
    },
    onInputAmount (v) {
      this.amount = valueSetterCutter(v)
      this.mix_clearServerError('amount')
      this.$v.amount.$touch()
    },
    onInputTotal (v) {
      this.total = valueSetterCutter(v)
      this.mix_clearServerError('total')
      this.$v.total.$touch()
    },
    valueSetter (val, mutation) {
      const value = valueSetterCutter(val)
      this.$store.commit(`buySell/${mutation}`, {
        formName: BUY_SELL_FORM_STOP_LIMIT,
        type: this.tradeType,
        value,
        currentPair: this.getCurrentPair,
      })
    },
    valueGetter (prop) {
      const val = this.$store.state.buySell.form[BUY_SELL_FORM_STOP_LIMIT][
        this.tradeType
      ][prop]
      // const isLastZero = this.isLastZeroChecker(val)
      // if (prop === 'price') {
      //     console.log('isLastZero::', isLastZero, val)
      // }
      return val
      // if (!val || isLastZero) return val
      // return new Decimal(val).toString()
    },
    async handleSubmit () {
      this.$v.$touch()
      if (this.$v.$invalid) return

      if (lockr.get('trade-confirmation-state')) {
        const confirmed = await this.showConfirmation()
        if (!confirmed) return
      }

      // REMOVED
      // // кастыль на фронте, при отправке с бай формы отнимать комиссию
      // const amount = (() => {
      //     const fee = getFeeIncludingPlc(
      //         this.getCurrentPair.stockTicker, // currentPairLeftSide,
      //         this.$store.state.buySell,
      //         this.tradeType,
      //     )
      //     // console.log(fee)
      //     if (this.tradeType === 'buy') {
      //         const val = new Decimal(this.amount).minus(new Decimal(this.amount).mul(fee)).toString()
      //         return val
      //     }
      //     return this.amount
      // })()
      const amount = this.amount
      // stop?: string
      // market: string;
      // direction: string;
      // amount: string;
      const body = {
        stop: this.stop,
        market: this.getCurrentPair.pairName,
        price: this.price,
        // total: this.total,
        direction: this.tradeType === 'buy' ? '2' : '1',
        amount,
      }

      this.loading = true
      const { errors, variables } = this.isMarginTrade
        ? await this.$api.putMarketCrossStopLimitOrder({ body })
        : await this.$api.putMarketStopLimitOrder({ body })

      this.$v.$reset()
      this.loading = false

      if (errors) {
        this.mix_setServerErrors(errors, variables)
        return
      }

      // this.amount = null
      this.$v.$reset()
    },
    amountQuickPicker ({ type }) {
      const fromBalance = this.balance.times(type).toString()
      if (this.tradeType === TRADE_TYPE.BUY) {
        this.total = valueSetterCutter(fromBalance)
      } else {
        this.amount = valueSetterCutter(fromBalance)
      }
    },
    showConfirmation () {
      const { amount, tradeType, getCurrentPair } = this
      const action =
        tradeType === TRADE_TYPE.BUY
          ? this.$t('trade.buy')
          : this.$t('trade.sell')
      const currency = getCurrentPair.stockTicker
      // const text =
      //   tradeType === 'buy'
      //     ? this.$t('common.confirmationModal.buyMarketText', {
      //       amount,
      //       buyTicker: getCurrentPair.stockTicker,
      //       sellTicker: getCurrentPair.moneyTicker,
      //     })
      //     : this.$t('common.confirmationModal.sellMarketText', {
      //       amount,
      //       buyTicker: getCurrentPair.moneyTicker,
      //       sellTicker: getCurrentPair.stockTicker,
      //     })
      const translateProps = {
        amount,
        amountTicker: getCurrentPair.stockTicker,
        // totalWithFee: this.price,
        totalTicker: getCurrentPair.moneyTicker,
      }
      const text =
        this.tradeType === TRADE_TYPE.BUY
          ? this.$t('common.confirmationModal.buyForText', translateProps)
          : this.$t('common.confirmationModal.sellForText', translateProps)
      return ConfirmationModal.open({
        title: `${action} ${currency}`,
        text,
        confirmationImg: 'buy.svg',
      })
    },
  },

  validations () {
    const onlyBuy =
      this.tradeType === TRADE_TYPE.BUY
        ? { total: { maxValue: maxValue(this.balance.toNumber()) } }
        : {}

    const onlySell =
      this.tradeType === TRADE_TYPE.SELL
        ? { amount: { maxValue: maxValue(this.balance.toNumber()) } }
        : {}
    return this.mix_validationsServerWrapper({
      stop: {
        required,
        greaterThanZero: v => new Decimal(v || 0).greaterThan(0),
      },
      price: {
        required,
        greaterThanZero: v => new Decimal(v || 0).greaterThan(0),
      },
      amount: {
        required,
        minValue: minValue(this.getCurrentPair.minAmountStock),
        ...onlySell.amount,
      },
      total: {
        required,
        ...onlyBuy.total,
      },
    })
  },
}
</script>

<style lang="scss" scoped>
.buy-sell-control-stop-limit {
  &__btn {
  }
}
</style>
